import bs58 from "bs58";
import {
  Connection,
  Keypair,
  LAMPORTS_PER_SOL,
  PublicKey,
  sendAndConfirmTransaction,
  SystemProgram,
  Transaction,
} from "@solana/web3.js";
import { Metaplex } from "@metaplex-foundation/js";
import {
  getOrCreateAssociatedTokenAccount,
  NATIVE_MINT,
  TOKEN_PROGRAM_ID,
  transfer,
} from "@solana/spl-token";
import {
  LIQUIDITY_STATE_LAYOUT_V4,
  MAINNET_PROGRAM_ID,
} from "@raydium-io/raydium-sdk";
import pumpfun from "@/store/modules/solana/pumpfun";
import raydium from "@/store/modules/solana/raydium";
import jupiter from "@/store/modules/solana/jupiter";

const PUMPSWAP_PROGRAM_ID = new PublicKey(
  "pAMMBay6oceH9fJKBRHGP5D4bD4sWpmSwMn52FMfXEA"
);

const solana = {
  namespaced: true,
  modules: {
    pumpfun: pumpfun,
    raydium: raydium,
    jupiter: jupiter,
  },
  state: {
    connection: null,
    tokenAddress: "",
    mint: null,
    pairAddress: "",
    rpcUrl: "",
    priorityFee: 0.003,
    slippage: 5,
    metadata: {
      name: "",
      symbol: "",
      totalSupply: 0,
      decimals: 0,
      logo: "",
      website: null,
      telegram: null,
      twitter: null,
    },
    isTokenCreatedOnPumpFun: false,
    tokenPriceInSOL: 0,
    tradeViaJupiter: false,
  },
  mutations: {
    setConnection(state, payload) {
      state.connection = payload;
    },
    setTokenAddress(state, payload) {
      state.tokenAddress = payload;
    },
    setMint(state, payload) {
      state.mint = payload;
    },
    setPairAddress(state, payload) {
      state.pairAddress = payload;
    },
    setRpcUrl(state, payload) {
      state.rpcUrl = payload;
    },
    setPriorityFee(state, payload) {
      state.priorityFee = payload;
    },
    setSlippage(state, payload) {
      state.slippage = payload;
    },
    setMetadata(state, payload) {
      state.metadata = payload;
    },
    setIsTokenCreatedOnPumpFun(state, payload) {
      state.isTokenCreatedOnPumpFun = payload;
    },
    setTokenPriceInSOL(state, payload) {
      state.tokenPriceInSOL = payload;
    },
    setTradeViaJupiter(state, payload) {
      state.tradeViaJupiter = payload;
    },
  },
  getters: {
    getDefaultData(state, getters, rootState) {
      return {
        tokenAddress: state.tokenAddress,
        rpcUrl: rootState.network.chain.rpcUrl,
        priorityFee: state.priorityFee,
        slippage: state.slippage,
      };
    },
    generateWallet() {
      const keypair = Keypair.generate();
      return {
        address: keypair.publicKey.toBase58(),
        privateKey: bs58.encode(keypair.secretKey),
      };
    },
    getSender: () => (privateKey) => {
      return Keypair.fromSecretKey(new Uint8Array(bs58.decode(privateKey)));
    },
    getBalanceNative: (state) => async (address) => {
      return (
        (await state.connection.getBalance(new PublicKey(address))) /
        LAMPORTS_PER_SOL
      );
    },
    getBalanceToken: (state) => async (address) => {
      const tokenAccounts =
        await state.connection.getParsedTokenAccountsByOwner(
          new PublicKey(address),
          { mint: state.mint }
        );
      return tokenAccounts.value.reduce((sum, account) => {
        const balance = account.account.data.parsed.info.tokenAmount.uiAmount;
        return sum + balance;
      }, 0);
    },
    validateAddress: () => (address) => {
      try {
        return PublicKey.isOnCurve(new PublicKey(address).toBuffer());
      } catch (e) {
        return false;
      }
    },
    getTxDetails: (state) => async (signature) => {
      const latestBlockHash = await state.connection.getLatestBlockhash();
      await state.connection.confirmTransaction(
        {
          blockhash: latestBlockHash.blockhash,
          lastValidBlockHeight: latestBlockHash.lastValidBlockHeight,
          signature: signature,
        },
        "confirmed"
      );

      return state.connection.getTransaction(signature, {
        maxSupportedTransactionVersion: 0,
        commitment: "confirmed",
      });
    },
  },
  actions: {
    async init({ state, getters, rootGetters, commit, dispatch }) {
      const data = Object.assign(
        {},
        getters.getDefaultData,
        rootGetters["network/getLocalStorage"]
      );
      const { tokenAddress, rpcUrl, priorityFee, slippage } = data;
      commit("setRpcUrl", rpcUrl);
      commit("setPriorityFee", priorityFee);
      commit("setSlippage", slippage);
      const connection = new Connection(state.rpcUrl, "confirmed");
      commit("setConnection", connection);
      if (tokenAddress) {
        await dispatch("setTokenAddress", tokenAddress);
      }
      await dispatch("network/setLocalStorage", data, {
        root: true,
      });
    },
    async setTokenAddress({ state, commit, dispatch }, tokenAddress) {
      commit("setTokenAddress", tokenAddress);
      if (tokenAddress) {
        commit("setMint", new PublicKey(tokenAddress));
        await dispatch("getTokenMetadata");
        if (state.isTokenCreatedOnPumpFun) {
          await dispatch("solana/pumpfun/init", null, { root: true });
        }
        await dispatch("getPairAddress");
        await dispatch("wallet/refreshBalanceTokenAllWallet", null, {
          root: true,
        });
        await dispatch(
          "network/updateLocalStorage",
          { tokenAddress },
          { root: true }
        );
      }
    },
    async getTokenMetadata({ state, commit }) {
      try {
        const metaplex = Metaplex.make(state.connection);
        const metadataAccount = metaplex
          .nfts()
          .pdas()
          .metadata({ mint: state.mint });
        const metadataAccountInfo = await state.connection.getAccountInfo(
          metadataAccount
        );
        if (metadataAccountInfo) {
          const token = await metaplex
            .nfts()
            .findByMint({ mintAddress: state.mint });
          commit(
            "setIsTokenCreatedOnPumpFun",
            token.updateAuthorityAddress.equals(
              new PublicKey("TSLvdd1pWpHVjahSpsvCXUbgwsL3JAcvokwaKt1eokM")
            )
          );
          const tokenSupply = await state.connection.getTokenSupply(state.mint);
          commit("setMetadata", {
            name: token.name,
            symbol: token.symbol,
            decimals: Number(token.mint.decimals),
            totalSupply: Number(tokenSupply.value.uiAmount),
            logo: token.json.image,
            website: token.json.website ?? "",
            twitter: token.json.twitter ?? "",
            telegram: token.json.telegram ?? "",
          });
        }
        // eslint-disable-next-line no-empty
      } catch (e) {}
      if (!state.metadata.symbol) {
        commit(
          "toast/add",
          {
            type: "danger",
            title: "The address is not a valid token contract",
          },
          { root: true }
        );
        commit("setTokenAddress", "");
      }
    },
    async getPairAddress({ state, commit, dispatch }) {
      try {
        await dispatch("getPairAddressPumpSwap");
        if (state.pairAddress) {
          return;
        }
        let accounts = await state.connection.getProgramAccounts(
          MAINNET_PROGRAM_ID.AmmV4,
          {
            commitment: "confirmed",
            filters: [
              { dataSize: LIQUIDITY_STATE_LAYOUT_V4.span },
              {
                memcmp: {
                  offset: LIQUIDITY_STATE_LAYOUT_V4.offsetOf("baseMint"),
                  bytes: NATIVE_MINT.toBase58(),
                },
              },
              {
                memcmp: {
                  offset: LIQUIDITY_STATE_LAYOUT_V4.offsetOf("quoteMint"),
                  bytes: state.mint.toBase58(),
                },
              },
            ],
          }
        );
        if (accounts.length === 0) {
          accounts = await state.connection.getProgramAccounts(
            MAINNET_PROGRAM_ID.AmmV4,
            {
              commitment: "confirmed",
              filters: [
                { dataSize: LIQUIDITY_STATE_LAYOUT_V4.span },
                {
                  memcmp: {
                    offset: LIQUIDITY_STATE_LAYOUT_V4.offsetOf("baseMint"),
                    bytes: state.mint.toBase58(),
                  },
                },
                {
                  memcmp: {
                    offset: LIQUIDITY_STATE_LAYOUT_V4.offsetOf("quoteMint"),
                    bytes: NATIVE_MINT.toBase58(),
                  },
                },
              ],
            }
          );
        }
        const rawData = accounts.map(({ pubkey, account }) => ({
          id: pubkey.toString(),
          data: LIQUIDITY_STATE_LAYOUT_V4.decode(account.data),
        }));
        commit("setPairAddress", rawData[0].id);
      } catch (error) {
        commit("setPairAddress", "");
      }
    },
    async getPairAddressPumpSwap({ state, commit }) {
      try {
        let accounts = await state.connection.getProgramAccounts(
          PUMPSWAP_PROGRAM_ID,
          {
            commitment: "confirmed",
            filters: [
              { dataSize: 211 },
              {
                memcmp: {
                  offset: 43,
                  bytes: state.mint.toBase58(),
                },
              },
            ],
          }
        );
        if (accounts.length === 0) {
          accounts = await state.connection.getProgramAccounts(
            PUMPSWAP_PROGRAM_ID,
            {
              commitment: "confirmed",
              filters: [
                { dataSize: 211 },
                {
                  memcmp: {
                    offset: 75,
                    bytes: state.mint.toBase58(),
                  },
                },
              ],
            }
          );
        }
        const rawData = accounts.map(({ pubkey, account }) => ({
          id: pubkey.toString(),
          data: LIQUIDITY_STATE_LAYOUT_V4.decode(account.data),
        }));
        commit("setPairAddress", rawData[0].id);
      } catch (error) {
        commit("setPairAddress", "");
      }
    },
    async getTokenPriceInSOL({ state, rootGetters, commit }) {
      try {
        const quoteData = await rootGetters["solana/jupiter/getQuoteData"]({
          inputMint: NATIVE_MINT.toBase58(),
          outputMint: state.mint.toBase58(),
          amount: LAMPORTS_PER_SOL,
          slippage: 0,
        });
        let tokenPriceInSOL =
          Number(quoteData.otherAmountThreshold) /
          Math.pow(10, state.metadata.decimals);
        tokenPriceInSOL =
          tokenPriceInSOL * ((100 - quoteData.priceImpactPct) / 100);
        commit("setTokenPriceInSOL", tokenPriceInSOL.toFixed(0));
      } catch (e) {
        commit("setTokenPriceInSOL", 0);
      }
    },
    async transferBalanceNative({ state, getters }, payload) {
      let { wallet, recipientAddress, amount } = payload;
      if (amount < 0) {
        throw new Error("Balance must be greater than or equal to 0");
      }
      if (!getters.validateAddress(recipientAddress)) {
        throw new Error("Recipient address is invalid!");
      }
      const sender = getters.getSender(wallet.privateKey);
      const balance = await getters.getBalanceNative(wallet.address);
      const { blockhash } = await state.connection.getLatestBlockhash();
      const transactionFee = await state.connection.getFeeForMessage(
        new Transaction({
          feePayer: sender.publicKey,
          recentBlockhash: blockhash,
        }).compileMessage()
      );
      if (amount > balance - transactionFee.value / LAMPORTS_PER_SOL) {
        amount = balance - transactionFee.value / LAMPORTS_PER_SOL;
      }
      const transaction = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: sender.publicKey,
          toPubkey: new PublicKey(recipientAddress),
          lamports: Math.floor(amount * LAMPORTS_PER_SOL),
        })
      );
      return await sendAndConfirmTransaction(
        state.connection,
        transaction,
        [sender],
        {
          skipPreflight: true,
          preflightCommitment: "confirmed",
        }
      );
    },
    async transferBalanceToken({ state, getters }, payload) {
      let { wallet, recipientAddress, amount } = payload;
      if (amount < 0) {
        throw new Error("Token balance must be greater than or equal to 0");
      }
      if (!getters.validateAddress(recipientAddress)) {
        throw new Error("Recipient address is invalid!");
      }
      const sender = getters.getSender(wallet.privateKey);
      const recipient = new PublicKey(recipientAddress);
      const senderTokenAccount = await getOrCreateAssociatedTokenAccount(
        state.connection,
        sender,
        state.mint,
        sender.publicKey
      );
      const recipientTokenAccount = await getOrCreateAssociatedTokenAccount(
        state.connection,
        sender,
        state.mint,
        recipient
      );
      return await transfer(
        state.connection,
        sender,
        senderTokenAccount.address,
        recipientTokenAccount.address,
        sender.publicKey,
        // eslint-disable-next-line no-undef
        BigInt(amount * Math.pow(10, state.metadata.decimals))
      );
    },
    async buy({ state, dispatch }, payload) {
      if (state.pairAddress) {
        if (state.tradeViaJupiter) {
          return await dispatch("solana/jupiter/buy", payload, { root: true });
        } else {
          return await dispatch("solana/raydium/buy", payload, { root: true });
        }
      } else {
        if (state.isTokenCreatedOnPumpFun) {
          return await dispatch("solana/pumpfun/buy", payload, { root: true });
        }
        throw new Error("No liquidity pool found for the given token pair.");
      }
    },
    async sell({ state, dispatch }, payload) {
      if (state.pairAddress) {
        if (state.tradeViaJupiter) {
          return await dispatch("solana/jupiter/sell", payload, { root: true });
        } else {
          return await dispatch("solana/raydium/sell", payload, { root: true });
        }
      } else {
        if (state.isTokenCreatedOnPumpFun) {
          return await dispatch("solana/pumpfun/sell", payload, { root: true });
        }
      }
      throw new Error("The contract cannot be swapped yet.");
    },
    async getListHolder({ state }) {
      const tokenAccounts = await state.connection.getParsedProgramAccounts(
        TOKEN_PROGRAM_ID,
        {
          filters: [
            { dataSize: 165 },
            {
              memcmp: {
                offset: 0,
                bytes: state.mint.toBase58(),
              },
            },
          ],
        }
      );
      const holders = tokenAccounts
        .filter(
          (account) =>
            account.account.data.parsed.info.tokenAmount.uiAmount >
            state.metadata.totalSupply * (1 / 10000)
        )
        .map((account) => ({
          address: account.account.data.parsed.info.owner,
          amount: Number(account.account.data.parsed.info.tokenAmount.uiAmount),
        }));
      holders.sort(function (a, b) {
        return b.amount - a.amount;
      });
      return holders.slice(0, 80);
    },
  },
};

export default solana;
